import type { ModuleName, Permission } from "@dentacore/shared-types";

/**
 * The help panel's articles — product content, shipped with the code.
 *
 * **Not clinic data.** Nothing here names a clinic's branch, chair, price or
 * person; every article describes a control the product draws. A story that
 * renames a control updates its article in the same pull request, which is
 * the whole maintenance plan (help-panel grill, 2026-11-02).
 *
 * `words` are what somebody might type instead of the title's own words —
 * synonyms, shorthand and the names of neighbouring things. Typos are the
 * search's job, not the list's.
 */
export interface HelpArticle {
  id: string;
  title: string;
  /** One sentence: what it is and why you would do it. */
  answer: string;
  /** At most five, each a single action. */
  steps: readonly string[];
  words: readonly string[];
  /** Path prefixes where it is suggested before anything is typed. */
  pages: readonly string[];
  /** Where "Take me there" goes. */
  to?: string;
  /** Holding any one of these lets the reader follow the steps. */
  needs?: readonly Permission[];
  /** The add-on it belongs to. */
  module?: ModuleName;
  /** Said instead of the steps to someone without `needs`. */
  whoDoesIt?: string;
}

const ADMIN_SYSTEM = "An administrator does this, in System";

export const HELP_ARTICLES: readonly HelpArticle[] = [
  // ── Calendar ────────────────────────────────────────────────────────────
  {
    id: "calendar-book",
    title: "Book an appointment",
    answer: "Put a patient in a chair at a time, with who treats them and what for.",
    steps: [
      "Click the empty slot in the right chair, or press New appointment.",
      "Find the patient by name, phone or file number.",
      "Choose who is treating them and the procedure — the length fills in.",
      "Save. The patient gets a WhatsApp confirmation.",
    ],
    words: ["book", "booking", "appointment", "new", "schedule", "add", "slot", "make"],
    pages: ["/calendar", "/"],
    to: "/calendar",
    needs: ["calendar.view"],
  },
  {
    id: "calendar-hold-chair",
    title: "Hold a chair (no patient)",
    answer: "Reserve chair time for cleaning, a meeting or maintenance so nobody books over it.",
    steps: [
      "Ctrl + click the empty slot, then Hold the chair.",
      "Pick Why from the block reasons, or Not saying.",
      "Save — it shows as ‘Chair held’ and on the chair's Google calendar.",
    ],
    words: ["hold", "block", "reserve", "busy", "unavailable", "buffer", "lunch", "meeting"],
    pages: ["/calendar"],
    to: "/calendar",
    needs: ["calendar.view"],
  },
  {
    id: "calendar-new-patient",
    title: "Book someone who has no file yet",
    answer: "Create the patient's file at the same time as their first booking.",
    steps: [
      "Ctrl + click the empty slot, then New patient here.",
      "Type their name and phone number.",
      "Choose who treats them and the procedure, then save.",
      "Add date of birth and flags when they arrive.",
    ],
    words: ["new", "patient", "first", "visit", "phone", "call", "caller", "unknown"],
    pages: ["/calendar"],
    to: "/calendar",
    needs: ["patients.create"],
  },
  {
    id: "calendar-multi-visit",
    title: "Book a crown, root canal or other multi-visit treatment",
    answer: "Booking a procedure that takes several visits books all of them at once.",
    steps: [
      "Book the first visit with the multi-visit procedure.",
      "Check the suggested date of each later visit, or clear one to book it later.",
      "Save — one confirmation goes for the whole chain.",
      "Visits left for later show on Home under Needs you.",
    ],
    words: [
      "crown",
      "root",
      "canal",
      "implant",
      "chain",
      "visits",
      "series",
      "course",
      "plan",
      "next",
    ],
    pages: ["/calendar", "/plans"],
    to: "/calendar",
    needs: ["calendar.view"],
  },
  {
    id: "calendar-family",
    title: "Book a family visit",
    answer: "Several patients back to back in one chair with one doctor, as one booking.",
    steps: [
      "Book the first person.",
      "Under Also in this visit, add each other person and their procedures.",
      "Save. There is no turnover between them, only after the last.",
      "To take one child out, open the booking and press Take out.",
    ],
    words: ["family", "children", "kids", "siblings", "parent", "group", "together"],
    pages: ["/calendar"],
    to: "/calendar",
    needs: ["calendar.view"],
  },
  {
    id: "calendar-reschedule",
    title: "Move an appointment to another time",
    answer: "Drag it, or use Reschedule to see only the free times in that chair.",
    steps: [
      "Drag the booking — it snaps to 15 minutes.",
      "Or open it and press Reschedule, then pick a free time.",
      "To move it to another day or location, cancel it and book again.",
    ],
    words: [
      "move",
      "reschedule",
      "change",
      "time",
      "drag",
      "later",
      "earlier",
      "postpone",
      "booking",
    ],
    pages: ["/calendar", "/"],
    to: "/calendar",
    needs: ["calendar.view"],
  },
  {
    id: "calendar-refused",
    title: "Why a booking was refused",
    answer:
      "A chair can hold one booking at a time plus its turnover (cleaning) gap; the message names the earliest free time.",
    steps: [
      "Read the time the message suggests and book there.",
      "Turnover is set per chair, in Edit chairs.",
      "Opening hours and stock never refuse a booking.",
    ],
    words: [
      "refused",
      "clash",
      "overlap",
      "error",
      "cannot",
      "turnover",
      "gap",
      "cleaning",
      "full",
    ],
    pages: ["/calendar"],
    to: "/calendar",
  },
  {
    id: "calendar-status",
    title: "Check in, seat and change a status",
    answer:
      "Move a booking through Confirmed, Checked in, In chair and Completed, one step at a time.",
    steps: [
      "Click the status on the booking or on Home's Today list.",
      "Move one step forward — or one step back if it was a mistake.",
      "No-show frees the chair; Completed and Cancelled are final.",
    ],
    words: [
      "status",
      "check",
      "checkin",
      "arrived",
      "seat",
      "chair",
      "noshow",
      "missed",
      "confirm",
    ],
    pages: ["/calendar", "/"],
    to: "/calendar",
    needs: ["calendar.view"],
  },
  {
    id: "calendar-complete",
    title: "Complete a visit and bill it",
    answer: "Completing charts the work, raises the bill and takes materials off the shelf.",
    steps: [
      "Open the booking and press Complete.",
      "List each procedure done and its tooth.",
      "Choose Nothing yet, Paid part or Paid in full, and the method.",
      "Press Record and complete.",
    ],
    words: ["complete", "finish", "done", "close", "bill", "invoice", "charge", "end"],
    pages: ["/calendar", "/"],
    to: "/calendar",
    needs: ["clinical.chart.edit"],
    whoDoesIt: "A clinician or the front desk does this.",
  },
  {
    id: "calendar-payment",
    title: "Take a payment",
    answer: "Record money received against a visit's invoice.",
    steps: [
      "Open the completed booking and press Record payment.",
      "The amount starts at the balance; lower it for a part payment.",
      "Choose the method and press Record.",
    ],
    words: ["pay", "payment", "paid", "receipt", "cash", "card", "money", "collect", "balance"],
    pages: ["/calendar", "/patients", "/"],
    to: "/calendar",
    needs: ["billing.payment.record"],
    whoDoesIt: "Someone allowed to record payments does this — usually the front desk.",
  },
  {
    id: "calendar-cancel",
    title: "Cancel a booking",
    answer: "Cancelling frees the slot at once and offers it to the waitlist.",
    steps: [
      "Open the booking and press Cancel the booking.",
      "For one visit of a chain, choose Only this one or cancel the rest.",
      "Cancelled is final — book again if it was a mistake.",
    ],
    words: ["cancel", "delete", "remove", "drop", "booking", "appointment"],
    pages: ["/calendar"],
    to: "/calendar",
    needs: ["calendar.view"],
  },
  {
    id: "calendar-waitlist",
    title: "Use the waitlist",
    answer: "Patients who want an earlier slot, offered a cancelled one in minutes.",
    steps: [
      "Press Waitlist, then Add someone with the days and times they can come.",
      "On a free slot, Ctrl + click → Fill from waitlist.",
      "Press Offer this slot next to whoever fits.",
    ],
    words: ["waitlist", "waiting", "list", "earlier", "cancellation", "gap", "fill", "queue"],
    pages: ["/calendar", "/"],
    to: "/calendar",
    needs: ["calendar.view"],
  },
  {
    id: "calendar-whatsapp",
    title: "Check WhatsApp bookings",
    answer: "The bot's bookings wait for staff to validate them; nothing changes until you agree.",
    steps: [
      "Press WhatsApp on the calendar's header.",
      "Under Awaiting validation, check each booking, then Validate or Decline.",
      "Under Waiting on a decision, Approve the move or Decline.",
    ],
    words: ["whatsapp", "bot", "agent", "queue", "requests", "validate", "approve", "chatbot"],
    pages: ["/calendar"],
    to: "/calendar",
    module: "CHATBOT",
  },
  {
    id: "calendar-remind",
    title: "Send a patient a reminder",
    answer: "Send one WhatsApp reminder now, on top of the automatic one the evening before.",
    steps: [
      "Open the booking.",
      "Press Send reminder.",
      "‘Reminder sent’ stays on screen — don't send twice.",
    ],
    words: ["remind", "reminder", "message", "text", "sms", "whatsapp", "notify"],
    pages: ["/calendar"],
    to: "/calendar",
    module: "CHATBOT",
  },
  {
    id: "calendar-edit-chairs",
    title: "Reorder chairs or change turnover",
    answer: "Set the order chairs draw in, their names, turnover and who sits where.",
    steps: [
      "Calendar ⋯ menu → Edit chairs.",
      "Drag a chair by its handle to reorder.",
      "Edit one to rename it or change its turnover minutes.",
    ],
    words: ["chair", "chairs", "order", "turnover", "cleaning", "rename", "room", "column"],
    pages: ["/calendar"],
    to: "/calendar",
    needs: ["settings.locations.manage"],
    whoDoesIt: `${ADMIN_SYSTEM} → Chairs.`,
  },
  {
    id: "calendar-locations",
    title: "See several locations in one calendar",
    answer: "Tick several locations in the top bar to see them side by side.",
    steps: [
      "Open the location picker in the top bar.",
      "Tick each location, or All my locations.",
      "Use Columns to lay it out by doctor or by chair.",
    ],
    words: ["location", "locations", "branch", "branches", "combined", "both", "all", "switch"],
    pages: ["/calendar"],
    to: "/calendar",
  },

  // ── Patients ────────────────────────────────────────────────────────────
  {
    id: "patients-new",
    title: "Add a new patient",
    answer: "Open a file with just the essentials; the rest is filled at the first exam.",
    steps: [
      "Patients → New patient.",
      "First name, last name, date of birth and phone.",
      "Create file. A shared number warns — Add anyway only for a family.",
    ],
    words: ["new", "patient", "file", "register", "create", "add", "registration"],
    pages: ["/patients"],
    to: "/patients",
    needs: ["patients.create"],
  },
  {
    id: "patients-flags",
    title: "Record an allergy or warning",
    answer: "Critical flags show on Home, the calendar and every booking form.",
    steps: [
      "Open the patient's file → Edit flags.",
      "Choose the kind and Critical or For information.",
      "Type the flag and what to do instead, then Add flag.",
    ],
    words: ["allergy", "allergic", "flag", "alert", "warning", "medical", "penicillin", "critical"],
    pages: ["/patients"],
    to: "/patients",
    needs: ["patients.flags.manage"],
  },
  {
    id: "patients-chart",
    title: "Record work on a tooth",
    answer: "Chart what was done now or what is still to be done, with its surfaces.",
    steps: [
      "Open the file's Chart and click the tooth.",
      "Under Record a procedure, choose what was done.",
      "Tap the parts of the tooth worked on.",
      "Choose Done now or To be done, then Add.",
    ],
    words: ["tooth", "teeth", "chart", "record", "filling", "surface", "procedure", "odontogram"],
    pages: ["/patients"],
    to: "/patients",
    needs: ["clinical.chart.edit"],
    whoDoesIt: "A clinician does this.",
  },
  {
    id: "patients-chart-remove",
    title: "Remove something charted by mistake",
    answer: "Removing a charted procedure deletes it for good — check before you press it.",
    steps: [
      "Open the tooth from the chart.",
      "Press Remove beside the procedure.",
      "Work written by a plan step is corrected on Treatment instead.",
    ],
    words: ["remove", "delete", "mistake", "wrong", "tooth", "undo", "charted"],
    pages: ["/patients"],
    to: "/patients",
    needs: ["clinical.chart.edit"],
    whoDoesIt: "A clinician does this.",
  },
  {
    id: "patients-notes",
    title: "Write or correct a clinical note",
    answer: "Notes are never lost: editing one saves a revision and keeps the original.",
    steps: [
      "Open the file → Notes → New note.",
      "Give it a title and write what was observed, done and advised.",
      "To correct it later, press Edit and Save revision.",
    ],
    words: ["note", "notes", "clinical", "write", "record", "observation", "edit"],
    pages: ["/patients"],
    to: "/patients",
    needs: ["clinical.notes.write"],
    whoDoesIt: "A clinician does this.",
  },
  {
    id: "patients-images",
    title: "Add X-rays or photos",
    answer: "Images live on the file's Imaging tab, by type and region.",
    steps: [
      "Open the file → Imaging → Import images.",
      "Choose JPEG, PNG or PDF files, up to 25 MB each.",
      "Set the type and region.",
    ],
    words: ["xray", "x-ray", "radiograph", "image", "images", "photo", "scan", "upload", "imaging"],
    pages: ["/patients"],
    to: "/patients",
    needs: ["clinical.imaging.upload"],
  },
  {
    id: "patients-void",
    title: "Fix a wrong invoice",
    answer: "A draft can be changed; an issued invoice is voided and its work billed again.",
    steps: [
      "Open the file → Billing.",
      "On a draft, change the line — a new price asks why.",
      "On an issued invoice, press Void…, then complete or add the work again.",
    ],
    words: ["invoice", "bill", "wrong", "void", "price", "correct", "mistake", "refund"],
    pages: ["/patients"],
    to: "/patients",
    needs: ["billing.invoice.void"],
    whoDoesIt: "Someone allowed to void invoices does this — ask an administrator.",
  },
  {
    id: "patients-merge",
    title: "Merge two files for the same person",
    answer: "Everything moves to the file you keep, after a preview. It cannot be undone.",
    steps: [
      "Patients → Possible duplicates → Review merge.",
      "Choose which file to keep.",
      "Pick each detail, then Merge files.",
    ],
    words: ["duplicate", "duplicates", "merge", "twice", "same", "two", "files", "combine"],
    pages: ["/patients"],
    to: "/patients",
    needs: ["patients.delete"],
    whoDoesIt: "An administrator does this, from Patients → Possible duplicates.",
  },

  // ── Treatment plans ─────────────────────────────────────────────────────
  {
    id: "plans-procedure",
    title: "Add or change a procedure and its price",
    answer: "The procedure list sets each treatment's length, price and visits for this location.",
    steps: [
      "Treatment plans → Add a procedure, or open one → Price and visits.",
      "Set the code, name, category and One tooth or The whole mouth.",
      "Type the number of visits and each visit's minutes, price and gap.",
      "Save changes.",
    ],
    words: [
      "procedure",
      "price",
      "prices",
      "catalogue",
      "list",
      "fee",
      "cost",
      "treatment",
      "code",
    ],
    pages: ["/plans"],
    to: "/plans",
    needs: ["procedures.manage"],
    whoDoesIt: "An administrator does this, on Treatment plans.",
  },

  // ── System ──────────────────────────────────────────────────────────────
  {
    id: "system-google",
    title: "Sync the chairs to Google Calendar",
    answer: "Each chair gets its own Google calendar, kept in step both ways.",
    steps: [
      "System → Google Calendar; type the practice's Google account.",
      "Press Create the calendars.",
      "Accept Google's emailed invitation in that account.",
      "Check the calendars appear in Google, then press I can see all.",
    ],
    words: [
      "google",
      "gcal",
      "calendar",
      "sync",
      "synchronise",
      "phone",
      "showing",
      "missing",
      "share",
    ],
    pages: ["/settings", "/calendar"],
    to: "/settings/google",
    needs: ["settings.integrations.manage"],
    whoDoesIt: `${ADMIN_SYSTEM} → Google Calendar.`,
  },
  {
    id: "system-reminders",
    title: "Turn on automatic reminders",
    answer: "Patients get a WhatsApp reminder the evening before, at the time you choose.",
    steps: [
      "System → Reminders, for the location in the top bar.",
      "Tick Send reminders automatically at this location.",
      "Set the time and press Save reminders.",
    ],
    words: ["reminder", "reminders", "automatic", "evening", "whatsapp", "sms", "noshow"],
    pages: ["/settings"],
    to: "/settings/reminders",
    module: "CHATBOT",
    needs: ["settings.locations.manage"],
    whoDoesIt: `${ADMIN_SYSTEM} → Reminders.`,
  },
  {
    id: "system-hours",
    title: "Set opening hours",
    answer: "Hours decide where the calendar's day starts and ends — they never refuse a booking.",
    steps: [
      "System → Opening hours.",
      "Fill in one day, then Use these times every day or Monday to Friday only.",
      "Mark closed days, then Save hours.",
    ],
    words: ["hours", "opening", "open", "close", "closed", "time", "day", "weekend"],
    pages: ["/settings"],
    to: "/settings/hours",
    needs: ["settings.locations.manage"],
    whoDoesIt: `${ADMIN_SYSTEM} → Opening hours.`,
  },
  {
    id: "system-chairs",
    title: "Add a chair",
    answer: "Each chair is a column on the calendar.",
    steps: [
      "System → Chairs → Add a chair.",
      "Name it and save.",
      "Set its turnover minutes and order on the calendar.",
    ],
    words: ["chair", "chairs", "room", "operatory", "add", "new", "column"],
    pages: ["/settings", "/calendar"],
    to: "/settings/chairs",
    needs: ["settings.locations.manage"],
    whoDoesIt: `${ADMIN_SYSTEM} → Chairs.`,
  },
  {
    id: "system-block-reasons",
    title: "Change the reasons for holding a chair",
    answer: "The reasons offered when a chair is held, and the only words sent to Google.",
    steps: [
      "System → Block reasons.",
      "Start from a suggested list, or Add a reason.",
      "Rename or retire any of them.",
    ],
    words: ["block", "reason", "reasons", "hold", "held", "why"],
    pages: ["/settings"],
    to: "/settings/block-reasons",
    needs: ["settings.locations.manage"],
    whoDoesIt: `${ADMIN_SYSTEM} → Block reasons.`,
  },
  {
    id: "people-invite",
    title: "Invite a member of staff",
    answer: "Each person signs in with their own account, seeing what their role allows.",
    steps: [
      "People → Staff → Add someone.",
      "Name, email, phone and their job, then Add and make a link.",
      "Open their page and give them access to each location.",
    ],
    words: [
      "staff",
      "invite",
      "user",
      "account",
      "add",
      "employee",
      "dentist",
      "login",
      "colleague",
    ],
    pages: ["/people", "/settings"],
    to: "/people/staff",
    needs: ["people.manage", "access.assign"],
    whoDoesIt: "An administrator does this, in People → Staff.",
  },
  {
    id: "people-access",
    title: "Change what someone can do",
    answer: "Roles are given per person per location; an exception adds or removes one thing.",
    steps: [
      "People → Staff → open the person.",
      "Under Roles by location, press Change.",
      "Or Add an exception, with why and until when.",
    ],
    words: ["access", "permission", "permissions", "role", "roles", "allow", "rights", "admin"],
    pages: ["/people", "/settings"],
    to: "/people/staff",
    needs: ["access.assign"],
    whoDoesIt: "An administrator does this, in People → Staff.",
  },

  // ── HR ──────────────────────────────────────────────────────────────────
  {
    id: "hr-rota",
    title: "Change and publish the rota",
    answer: "Set each person's regular week once, change single days, then publish.",
    steps: [
      "HR → Shifts → click the person.",
      "Edit every week for their regular hours, or click one day to change it.",
      "Press Publish this week so the team sees it.",
    ],
    words: ["rota", "shift", "shifts", "schedule", "hours", "week", "publish", "roster"],
    pages: ["/hr"],
    to: "/hr/shifts",
    needs: ["staff.rota.edit.any"],
    whoDoesIt: "A manager does this, in HR → Shifts.",
  },
  {
    id: "hr-ask-leave",
    title: "Ask for time off",
    answer: "Your request waits for a manager; your balance updates when it is decided.",
    steps: [
      "HR → Me → Ask for time off.",
      "Choose the type, first and last day, and a reason.",
      "Press Ask. Withdraw it while it is still waiting.",
    ],
    words: ["leave", "holiday", "vacation", "off", "time", "sick", "absence", "request", "day"],
    pages: ["/hr"],
    to: "/hr/me",
    needs: ["staff.timeoff.request"],
  },
  {
    id: "hr-decide-leave",
    title: "Approve or decline time off",
    answer: "Requests wait on HR → Requests with each person's leave left beside them.",
    steps: [
      "HR → Requests → Waiting on a decision.",
      "Approve or Decline — it warns if they have patients booked.",
      "Reopen puts a decided one back in the queue.",
    ],
    words: ["approve", "decline", "leave", "holiday", "request", "requests", "balance"],
    pages: ["/hr"],
    to: "/hr/requests",
    needs: ["staff.timeoff.decide"],
    whoDoesIt: "A manager decides this, in HR → Requests.",
  },

  // ── Supply ──────────────────────────────────────────────────────────────
  {
    id: "supply-low-stock",
    title: "Order something that is running low",
    answer: "Low items show on Home and in Inventory; order from the cheapest supplier.",
    steps: [
      "Home → Elsewhere → Order it, or Inventory → the item → Suppliers.",
      "Pick the supplier and the number of packs.",
      "Copy the message, send it, then Mark as ordered.",
    ],
    words: ["stock", "low", "order", "running", "out", "supplies", "inventory", "buy", "reorder"],
    pages: ["/inventory", "/supply", "/orders", "/"],
    to: "/inventory",
    module: "SUPPLY",
    needs: ["inventory.movement.record"],
  },
  {
    id: "supply-receive",
    title: "Put a delivery on the shelf",
    answer: "Receiving raises the stock count and clears the low notice.",
    steps: [
      "Home or Order history → It arrived.",
      "Type the packs received and the invoice price per pack.",
      "Press Add to the shelf.",
    ],
    words: ["delivery", "arrived", "receive", "received", "box", "shelf", "order"],
    pages: ["/inventory", "/orders", "/"],
    to: "/orders/history",
    module: "SUPPLY",
    needs: ["inventory.movement.record"],
  },
  {
    id: "supply-count",
    title: "Correct a stock count",
    answer: "Every change is a movement with your name on it; stock can go below zero.",
    steps: [
      "Inventory → click the item.",
      "On Stock, press Add or Remove.",
      "Mark a bulk pack with Opened one when you open it.",
    ],
    words: ["count", "stock", "wrong", "correct", "adjust", "opened", "inventory", "quantity"],
    pages: ["/inventory"],
    to: "/inventory",
    module: "SUPPLY",
    needs: ["inventory.movement.record"],
  },

  // ── Accounting ──────────────────────────────────────────────────────────
  {
    id: "accounting-expense",
    title: "Record an expense",
    answer: "Rent, lab fees and other costs count in monthly and yearly profit.",
    steps: [
      "Accounting → Add expense.",
      "Amount, date paid and category.",
      "Choose the location, or Shared by all locations.",
    ],
    words: ["expense", "expenses", "rent", "lab", "cost", "bill", "spend", "pay"],
    pages: ["/accounting"],
    to: "/accounting",
    module: "ACCOUNTING",
    needs: ["accounting.manage"],
    whoDoesIt: "An administrator or finance officer does this, in Accounting.",
  },
  {
    id: "accounting-salary",
    title: "Set or change a salary",
    answer: "A salary counts from the date it takes effect; months before keep the old one.",
    steps: [
      "Accounting → Salaries → the person → Change salary.",
      "Type the new monthly salary and the date it takes effect.",
      "When someone leaves, Set last day so it stops counting.",
    ],
    words: ["salary", "salaries", "wage", "pay", "raise", "payroll", "last", "leaving"],
    pages: ["/accounting"],
    to: "/accounting",
    module: "ACCOUNTING",
    needs: ["accounting.salaries.manage"],
    whoDoesIt: "An administrator or finance officer does this, in Accounting → Salaries.",
  },
  {
    id: "accounting-month",
    title: "Read the month's profit",
    answer: "Profit is money received minus materials, salaries and expenses.",
    steps: [
      "Accounting → Monthly, and choose the month.",
      "Check Still owed for anything 31 days or older.",
      "Daily figures leave out salaries and expenses, so days don't add up to the month.",
    ],
    words: ["profit", "month", "monthly", "revenue", "income", "owed", "report", "figures"],
    pages: ["/accounting"],
    to: "/accounting",
    module: "ACCOUNTING",
    needs: ["accounting.view"],
    whoDoesIt: "Only administrators and finance officers see Accounting.",
  },
  {
    id: "accounting-targets",
    title: "Set accounting targets",
    answer:
      "Targets show against the cards: collection rate, overhead, and money owed over 60 days.",
    steps: ["Accounting → Targets.", "Type each target, or leave it blank for none.", "Save."],
    words: ["target", "targets", "goal", "overhead", "collection", "rate"],
    pages: ["/accounting"],
    to: "/accounting",
    module: "ACCOUNTING",
    needs: ["accounting.manage"],
    whoDoesIt: "An administrator or finance officer does this, in Accounting.",
  },

  // ── Home ────────────────────────────────────────────────────────────────
  {
    id: "home-today",
    title: "Use Home's Today list",
    answer: "Everyone still to be seen today, with their alerts, and what to do next for each.",
    steps: [
      "Change a status from the pill on each row.",
      "Book next books their next visit; No-show frees the chair.",
      "The right-hand card lists what needs you, the rest of the clinic, and attendance.",
    ],
    words: ["home", "today", "dashboard", "remaining", "next", "list", "day"],
    pages: ["/"],
    to: "/",
    needs: ["home.view"],
  },
  {
    id: "home-elsewhere",
    title: "Clear a notice on Home",
    answer:
      "Elsewhere lists what is outstanding anywhere in the clinic; each notice says what to do.",
    steps: [
      "Home → Elsewhere.",
      "Use the button on the notice — Order it, It arrived, Confirm, See the task.",
      "Mark done once it is dealt with.",
    ],
    words: ["notice", "notices", "alert", "alerts", "elsewhere", "warning", "clear", "dismiss"],
    pages: ["/"],
    to: "/",
    needs: ["home.view"],
  },

  // ── Getting around ──────────────────────────────────────────────────────
  {
    id: "shell-ring",
    title: "Use the quick ring",
    answer: "Ctrl + click anything to see what you can do to it, in a ring around the pointer.",
    steps: [
      "Hold Ctrl and click a booking, a slot, a tooth or a row — or press Ctrl + .",
      "Pick a wedge; the first is the usual next step.",
      "Arrange this ring puts the wedges in your own order.",
    ],
    words: ["ring", "quick", "shortcut", "ctrl", "menu", "actions", "right", "click"],
    pages: ["/", "/calendar", "/patients", "/inventory"],
  },
  {
    id: "shell-location",
    title: "Switch location",
    answer:
      "The location picker in the top bar chooses whose calendar, patients' bills and stock you see.",
    steps: [
      "Open the location picker at the top left of the bar.",
      "Pick one location, or tick several to see them in one calendar.",
      "Your choice is remembered.",
    ],
    words: ["location", "branch", "switch", "change", "clinic", "site", "other"],
    pages: ["/", "/calendar"],
  },
  {
    id: "shell-theme",
    title: "Change light or dark, or sign out",
    answer: "Theme, style and Sign out are in your account menu.",
    steps: [
      "Click your initials at the top right.",
      "Choose Theme (light or dark) or Style (Solid or Glass).",
      "Sign out on a shared computer at the end of the day.",
    ],
    words: ["theme", "dark", "light", "mode", "style", "glass", "sign", "logout", "out"],
    pages: ["/"],
  },

  // ── Patients, further ───────────────────────────────────────────────────
  {
    id: "patients-invoice",
    title: "Make an invoice by hand",
    answer: "Start a draft, add lines from the catalogue or Other, then issue it.",
    steps: [
      "Open the file → Billing → New invoice.",
      "Add a line: a procedure, or Other with what it is for and the amount.",
      "Issue it when it is right — an issued invoice is only voided, never edited.",
    ],
    words: ["invoice", "bill", "new", "draft", "issue", "line", "charge", "statement"],
    pages: ["/patients"],
    to: "/patients",
    needs: ["billing.invoice.create"],
    whoDoesIt: "Someone allowed to create invoices does this — usually the front desk.",
  },
  {
    id: "patients-erase",
    title: "Erase a patient who asks to be forgotten",
    answer:
      "Their name and contact details go; the chart, notes and invoices stay. It cannot be undone.",
    steps: [
      "Open the file → Edit → Erase identity.",
      "Read what goes and what stays.",
      "Confirm. The file becomes ‘Erased patient #…’.",
    ],
    words: ["erase", "forget", "forgotten", "gdpr", "privacy", "delete", "remove", "personal"],
    pages: ["/patients"],
    to: "/patients",
    needs: ["patients.delete"],
    whoDoesIt: "Only an administrator can erase a patient's identity.",
  },
  {
    id: "patients-baby-teeth",
    title: "Show baby teeth",
    answer: "Baby teeth show by themselves for children under 13, and can be switched per patient.",
    steps: [
      "Open the file's Chart.",
      "Press Baby teeth to switch the layer on or off.",
      "Mark a baby tooth fallen when the adult one comes through.",
    ],
    words: ["baby", "child", "children", "milk", "primary", "deciduous", "fallen", "kid"],
    pages: ["/patients"],
    to: "/patients",
    needs: ["clinical.chart.view"],
  },

  // ── Treatment plans, further ────────────────────────────────────────────
  {
    id: "plans-import",
    title: "Import a price list",
    answer:
      "A CSV with code, label, durationMinutes and price adds new procedures and updates existing ones.",
    steps: [
      "Treatment plans → Import a CSV.",
      "Columns: code, label, durationMinutes, price, and category if you like.",
      "Read the result — every skipped line says why.",
    ],
    words: ["import", "csv", "excel", "spreadsheet", "price", "list", "upload", "bulk"],
    pages: ["/plans"],
    to: "/plans",
    needs: ["procedures.manage"],
    whoDoesIt: "An administrator does this, on Treatment plans.",
  },

  // ── Tasks and workflows ─────────────────────────────────────────────────
  {
    id: "tasks-work",
    title: "Take and finish a task",
    answer: "Take a task to make it yours; Resolve when done, Close with a reason when not needed.",
    steps: [
      "Tasks → Mine and my roles'.",
      "Take it.",
      "Resolve when the job is done, or Close it and say why.",
    ],
    words: ["task", "tasks", "todo", "job", "resolve", "close", "take", "assigned"],
    pages: ["/tasks"],
    to: "/tasks",
  },
  {
    id: "tasks-new",
    title: "Give someone a task",
    answer: "A task goes to a role, for anyone in it to take, or straight to one person.",
    steps: [
      "Tasks → New task.",
      "Type, title and what to do.",
      "Give it to a role or a person, set the due date and location, then Create task.",
    ],
    words: ["task", "new", "assign", "give", "delegate", "remind", "colleague"],
    pages: ["/tasks"],
    to: "/tasks",
  },
  {
    id: "workflows-build",
    title: "Automate a routine with a workflow",
    answer: "A workflow runs steps by itself — on a change, on a schedule, or from the quick ring.",
    steps: [
      "Workflows → New workflow; name it and choose what it runs on.",
      "Choose how it starts, then add steps, waits and forks.",
      "Test run, then Save and switch it on.",
    ],
    words: ["workflow", "workflows", "automate", "automation", "rule", "trigger", "routine"],
    pages: ["/workflows"],
    to: "/workflows",
    needs: ["settings.clinic.manage"],
    whoDoesIt: "Only an administrator builds workflows.",
  },

  // ── HR, further ─────────────────────────────────────────────────────────
  {
    id: "hr-certificate",
    title: "Renew a certificate",
    answer: "Upload the renewal; a manager checks it and the new expiry counts from then.",
    steps: [
      "HR → Me → Certificates → Upload renewal.",
      "Choose the PDF, JPEG or PNG and type the new expiry.",
      "It waits for a manager's check; the old date stands until then.",
    ],
    words: ["certificate", "licence", "license", "renew", "expiry", "expired", "compliance"],
    pages: ["/hr"],
    to: "/hr/me",
    needs: ["hr.self"],
  },
  {
    id: "hr-sick",
    title: "Record someone off sick",
    answer: "A colleague or manager records sickness — nobody records their own.",
    steps: [
      "HR → Staff management → the person.",
      "Record off sick.",
      "Back at work when they return.",
    ],
    words: ["sick", "ill", "absent", "absence", "illness", "off"],
    pages: ["/hr"],
    to: "/hr/staff",
    needs: ["hr.absence.record"],
    whoDoesIt: "A manager records sickness, in HR → Staff management.",
  },

  // ── Supply, further ─────────────────────────────────────────────────────
  {
    id: "supply-cart",
    title: "Order several things at once",
    answer: "The cart makes one order per supplier; DentaCore writes the message and you send it.",
    steps: [
      "Add items to the cart from the Catalog or Inventory.",
      "Orders → Cart; set the packs, and say why for any non-cheapest supplier.",
      "Place the orders, copy each message, send it, then Mark as sent.",
    ],
    words: ["cart", "basket", "order", "orders", "buy", "purchase", "supplier", "checkout"],
    pages: ["/orders", "/supply", "/inventory"],
    to: "/orders/cart",
    module: "SUPPLY",
    needs: ["inventory.movement.record"],
  },
  {
    id: "supply-supplier",
    title: "Add a supplier and their prices",
    answer: "Each supplier's own prices let DentaCore point out the cheapest for every item.",
    steps: [
      "People → Suppliers → Add a supplier.",
      "Products → Supplier items → add what they sell, or import their list.",
      "Give each its pack, uses per pack and price.",
    ],
    words: ["supplier", "suppliers", "vendor", "depot", "price", "prices", "catalog", "catalogue"],
    pages: ["/people/suppliers", "/supply"],
    to: "/people/suppliers",
    module: "SUPPLY",
    needs: ["inventory.items.manage"],
    whoDoesIt: "An administrator sets suppliers up.",
  },
  {
    id: "supply-tray",
    title: "Set what a procedure's tray uses",
    answer:
      "The tray is the prep list on every booking, and what a completed visit takes off the shelf.",
    steps: [
      "Inventory → Procedure trays → pick the procedure → Edit tray.",
      "Add a material, with how many uses and per visit or per tooth.",
      "Done.",
    ],
    words: ["tray", "trays", "materials", "prep", "setup", "uses", "consumables"],
    pages: ["/inventory", "/plans"],
    to: "/inventory",
    module: "SUPPLY",
    needs: ["inventory.items.manage"],
    whoDoesIt: "An administrator sets trays up.",
  },

  // ── System, further ─────────────────────────────────────────────────────
  {
    id: "system-location",
    title: "Open a new location",
    answer:
      "A location has its own chairs, hours, prices, stock and currency; staff and patients are shared.",
    steps: [
      "System → Locations → Open a location.",
      "Name, address, Google Maps link, timezone, country and currency.",
      "Then add its chairs, hours and procedures.",
    ],
    words: ["location", "branch", "new", "open", "site", "clinic", "second"],
    pages: ["/settings"],
    to: "/settings/locations",
    needs: ["settings.clinic.manage"],
    whoDoesIt: `${ADMIN_SYSTEM} → Locations.`,
  },
  {
    id: "system-retire-chair",
    title: "Retire a chair or close a location",
    answer: "Retiring keeps the history; it is refused while bookings still lie ahead.",
    steps: [
      "Move or cancel the bookings ahead first — the message gives the count.",
      "System → Chairs → the chair → Retire it, or the location → Close location.",
      "Bring back or Reopen undoes it.",
    ],
    words: ["retire", "remove", "delete", "close", "chair", "location", "branch"],
    pages: ["/settings"],
    to: "/settings/chairs",
    needs: ["settings.locations.manage"],
    whoDoesIt: `${ADMIN_SYSTEM} → Chairs or Locations.`,
  },
  {
    id: "system-modules",
    title: "Add Supply, Accounting or the chatbot",
    answer: "Add-ons are switched on by DentaCore when an administrator asks.",
    steps: [
      "System → Modules, or click a locked item in the sidebar.",
      "Ask for the add-on.",
      "It shows ‘You have asked for it’ until it is switched on.",
    ],
    words: ["module", "modules", "addon", "add-on", "locked", "supply", "accounting", "chatbot"],
    pages: ["/settings"],
    to: "/settings/modules",
    needs: ["settings.clinic.manage"],
    whoDoesIt: `${ADMIN_SYSTEM} → Modules.`,
  },
  {
    id: "system-roles",
    title: "Create or change a role",
    answer: "Roles are the clinic's own; each says what it allows, area by area.",
    steps: [
      "System → Roles and access.",
      "New role, starting from the closest one, or open a role → Edit permissions.",
      "Give it to people on their page in People → Staff.",
    ],
    words: ["role", "roles", "permission", "permissions", "access", "rights"],
    pages: ["/settings"],
    to: "/settings/access",
    needs: ["access.roles.manage"],
    whoDoesIt: `${ADMIN_SYSTEM} → Roles and access.`,
  },
  {
    id: "system-assistant",
    title: "Let staff use the WhatsApp assistant",
    answer:
      "Staff message the clinic's number from their own phone to book, move or look things up.",
    steps: [
      "People → Staff → the person.",
      "Type their mobile under WhatsApp assistant and save.",
      "It acts with their permissions and asks no confirmation — tell them to be precise.",
    ],
    words: ["assistant", "whatsapp", "staff", "phone", "bot", "mobile"],
    pages: ["/people", "/settings"],
    to: "/people/staff",
    module: "CHATBOT",
    needs: ["people.manage"],
    whoDoesIt: "An administrator does this, in People → Staff.",
  },

  // ── Account ─────────────────────────────────────────────────────────────
  {
    id: "account-password",
    title: "Change your password",
    answer: "At least 12 characters; length matters, not symbols.",
    steps: [
      "Signed out: Forgotten your password? on the sign-in page.",
      "Type your email and open the link we send.",
      "Choose the new password twice and save.",
    ],
    words: ["password", "forgot", "forgotten", "reset", "login", "sign", "locked"],
    pages: ["/"],
  },
];
